import axios from "axios"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Countries } from "../../../../enums/dropdownValues"
import { AppDispatch } from "../../../../store/Store"
import { FileInfo, isValidEmail } from "../../../../utils/utils"
import { useGetCitiesQuery, useGetContactTypesQuery, useLazyGetCountryStatesQuery } from "../../../Common/commonService"
import { getAllCities, getCities, getDestinationCities } from "../../../Common/commonSlice"
import { setAddAccountForm } from "../../AccountSlice"
import {
    useAddAccountMutation,
    useEditAccountMutation,
    useEditBankDetailsMutation,
    useGetBankDetailsQuery,
    useGetFormParentAccountQuery,
} from "../../AccountsServices"
import usePermissions from "permissions/usePermissions"

type VendorDetailType = {
    services: { id: number; title: string }[]
    department: { id: string; title: string }[]
    zone: { id: string; title: string }[]
    state: { id: number; title: string; [key: string]: any }[]
    city: { id: number; title: string; state_id?: number; [key: string]: any }[]
    pan: string
    msme_reg: { id: string; title: string }
    msme_reg_number: string
    msme_reg_type: { id: string; title: string }
    gst_treatment: { id: string; title: string }
}

const useAddAccounts = ({ ticketData, editFlag, toggle, onSuccess }: any) => {
    const dispatch = useDispatch<AppDispatch>()
    const permissions = usePermissions()
    const addAllAccountsPermission = permissions.ACCOUNTS__ADD_ALL_ACCOUNTS
    const addClientAccountsPermission = permissions.ACCOUNTS__ADD_CLIENT_ACCOUNTS
    const addVendorAccountsPermission = permissions.ACCOUNTS__ADD_VENDOR_ACCOUNTS
    const { addAccountFormValue } = useSelector((store: any) => store.accounts)
    const cities = useSelector(getAllCities)

    const [toggleError, setToggleError] = useState(false)
    const [isChecked, setIsChecked] = useState(false)
    const [inActiveAddress, setInActiveAddress] = useState<any[]>([])
    const [inActiveContacts, setInActiveContacts] = useState<any[]>([])
    const [formValues, setFormValues] = useState<any[]>([
        {
            country: { id: 101, title: "India" },
            address: "",
            pincode: "",
            city_id: { id: "", title: "" },
            state_id: { id: "", title: "" },
            shippingCities: [],
            shippingStates: [],
            is_active: 1,
            is_primary: 1,
            gstin: "",
            gst_certificate_url: "",
            gst_certificate_path: "",
        },
    ])
    const [contactDetails, setContactDetails] = useState<any[]>([
        {
            name: "",
            phone: "",
            country_code: "+91",
            mapping_id: "",
            email: "",
            contact_type: { id: "", title: "" },
            is_active: 1,
            is_primary: 1,
        },
    ])

    const [bankDetails, setBankDetails] = useState<any[]>([
        {
            bank_name: "",
            account_number: "",
            ifsc_code: "",
            account_holder_name: "",
        },
    ])

    const [prefillDone, setPrefillDone] = useState(false)

    const [vendorDetails, setVendorDetails] = useState<VendorDetailType>({
        services: [],
        department: [],
        zone: [],
        state: [],
        city: [],
        pan: "",
        msme_reg: { id: "", title: "" },
        msme_reg_number: "",
        msme_reg_type: { id: "", title: "" },
        gst_treatment: { id: "", title: "" },
    })
    const { data: vendorCitiesList } = useGetCitiesQuery(
        {
            state_id: vendorDetails?.state?.map((i: any) => i.id),
        },
        {
            skip: !vendorDetails.state?.length,
        }
    )

    const { data: bankDetailsData } = useGetBankDetailsQuery(
        {
            account_id: ticketData?.id,
        },
        {
            skip: !ticketData?.id || !editFlag,
        }
    )

    const { data: getLinkedAccountData } = useGetFormParentAccountQuery()
    const { data: contactTypes } = useGetContactTypesQuery()
    const [getCountryStates, { data: vendorStateList }] = useLazyGetCountryStatesQuery()
    const [addAccount, { isLoading: isAddAccountLoading, isSuccess: isAddAccountSuccess }] = useAddAccountMutation()
    const [editAccount, { isLoading: isEditAccountLoading, isSuccess: isEditAccountSuccess }] = useEditAccountMutation()
    const [editBankDetails] = useEditBankDetailsMutation()
    const [showLocationDetails, setShowLocationDetails] = useState<boolean[]>(formValues.map(() => false))
    const [showContactDetails, setShowContactDetails] = useState<boolean[]>(contactDetails.map(() => false))
    const [showBankDetails, setShowBankDetails] = useState<boolean[]>(bankDetails.map(() => false))
    const isAccountLoading = editFlag ? isEditAccountLoading : isAddAccountLoading
    const isAccountSuccess = editFlag ? isEditAccountSuccess : isAddAccountSuccess

    let handleChanges = (i: number, e: any) => {
        let newFormValues: any = [...formValues]
        newFormValues[i][e.target.name] = e.target.value
        const re = /^[0-9\b]+$/
        const alphaNumRegex = /^[A-Za-z0-9]*$/

        if (e.target.name === "pincode") {
            if (re.test(e.target.value) || e.target.value === "") {
                setFormValues(newFormValues)
            }
        } else if (e.target.name === "gstin") {
            if (e.target.value === "" || (e.target.value.length <= 15 && alphaNumRegex.test(e.target.value))) {
                setFormValues(newFormValues)
            }
        } else {
            setFormValues(newFormValues)
        }
    }
    let handleCountryChanges = async (i: number, countryObj: any) => {
        const updated = [...formValues]
        updated[i].country = countryObj
        updated[i].state_id = { id: "", title: "" }
        updated[i].city_id = { id: "", title: "" }
        updated[i].shippingCities = []

        const response = await getCountryStates({ country_id: countryObj.id }).unwrap()
        updated[i]["shippingStates"] = response
        setFormValues(updated)
    }

    const handleBankChanges = (i: number, e: any) => {
        let newFormValues: any = [...bankDetails]
        newFormValues[i] = { ...newFormValues[i], [e.target.name]: e.target.value }
        setBankDetails(newFormValues)
    }

    const handleTogglePrimary = (index: number) => {
        if (formValues.length === 1 && index === 0) {
            return
        }

        const updatedFormValues = [...formValues]
        updatedFormValues[index].is_primary = updatedFormValues[index].is_primary === 1 ? 0 : 1

        if (updatedFormValues[index].is_primary === 1) {
            updatedFormValues.forEach((item, idx) => {
                if (idx !== index) item.is_primary = 0
            })
        }

        setFormValues(updatedFormValues)
    }
    const handleContactTogglePrimary = (index: number) => {
        if (contactDetails.length === 1 && index === 0) {
            return
        }

        const updatedFormValues = [...contactDetails]
        updatedFormValues[index].is_primary = updatedFormValues[index].is_primary === 1 ? 0 : 1

        if (updatedFormValues[index].is_primary === 1) {
            updatedFormValues.forEach((item, idx) => {
                if (idx !== index) item.is_primary = 0
            })
        }

        setContactDetails(updatedFormValues)
    }
    const changeshippingStateHandle = (i: number, e: any) => {
        let newFormValues: any = [...formValues]
        newFormValues[i]["state_id"] = e
        newFormValues[i]["city_id"] = { id: "", title: "" }
        newFormValues[i]["shippingCities"] = []
        dispatch(getDestinationCities(e?.id)).then((response: any) => {
            const updatedFormValues = [...formValues]
            updatedFormValues[i]["shippingCities"] = response?.payload || []
            setFormValues(updatedFormValues)
        })
        setFormValues(newFormValues)
    }
    const changeshippingCityHandle = (i: number, e: any) => {
        let newFormValues: any = [...formValues]
        newFormValues[i]["city_id"] = e
        setFormValues(newFormValues)
    }
    let handleFileChanges = (i: number, files: FileInfo[], name: string) => {
        let newFormValues: any = [...formValues]
        newFormValues[i][name] = files
        setFormValues(newFormValues)
    }

    let addFormFields = async () => {
        const indiaStates = await getCountryStates({ country_id: 101 }).unwrap()
        setFormValues([
            ...formValues,
            {
                country: { id: 101, title: "India" },
                address: "",
                pincode: "",
                state_id: { id: "", title: "" },
                city_id: { id: "", title: "" },
                gstin: "",
                gst_certificate_url: "",
                gst_certificate_path: "",
                is_active: 1,
                is_primary: 0,
                shippingStates: indiaStates,
            },
        ])
    }

    let addContactFields = () => {
        setContactDetails([
            ...contactDetails,
            {
                name: "",
                phone: "",
                email: "",
                contact_type: { id: "", title: "" },
                country_code: "+91",
                mapping_id: "",
                is_active: 1,
                is_primary: 0,
            },
        ])
    }

    const addBankDetails = () => {
        setBankDetails([
            ...bankDetails,
            {
                bank_name: "",
                account_number: "",
                ifsc_code: "",
                account_holder_name: "",
            },
        ])
    }

    const removeFormFields = (index: number) => {
        if (editFlag) {
            const updatedFormValue = [...formValues, ...inActiveAddress]
            updatedFormValue[index].is_active = 0
            const activeFormValues = updatedFormValue.filter(item => item.is_active === 0)
            setInActiveAddress(activeFormValues)
        }
        const updatedFormValues = [...formValues]
        updatedFormValues.splice(index, 1)
        setFormValues(updatedFormValues)
    }

    const removeContactFields = (index: number) => {
        if (editFlag) {
            const updatedFormValue = [...contactDetails, ...inActiveContacts]
            updatedFormValue[index].is_active = 0
            const activeFormValues = updatedFormValue.filter(item => item.is_active === 0)
            setInActiveContacts(activeFormValues)
        }
        const updatedFormValues = [...contactDetails]
        updatedFormValues.splice(index, 1)
        setContactDetails(updatedFormValues)
    }

    const removeBankDetails = (index: number) => {
        const updatedFormValues = [...bankDetails]
        updatedFormValues.splice(index, 1)
        setBankDetails(updatedFormValues)
    }

    const handleDisable = () => {
        const accountTypeId = addAccountFormValue?.account_type?.id

        if (
            addAccountFormValue?.account_name === "" ||
            addAccountFormValue?.legal_business_name === "" ||
            !accountTypeId
        ) {
            return true
        }

        if (accountTypeId === "Vendor") {
            const { services, department, msme_reg, msme_reg_number, msme_reg_type } = vendorDetails

            if (msme_reg?.id === "1" && (!msme_reg_number || !msme_reg_type?.id)) {
                return true
            }
            if (!services?.length || !department?.length) {
                return true
            }
        }

        return false
    }

    const areAllContactFieldsFilled = () => {
        const activeFormValues = contactDetails.filter((item: any) => item.is_active === 1)
        const primaryCount = activeFormValues.filter((item: any) => item.is_primary === 1).length

        if (primaryCount !== 1) {
            return false
        }
        for (const formData of activeFormValues) {
            for (const key in formData) {
                if (formData.hasOwnProperty(key) && formData[key as keyof FormData] === "") {
                    const value = formData[key as keyof FormData]
                    if (key === "phone") {
                        if (value === "" || value?.length !== 10) {
                            return false
                        }
                    } else if (key === "contact_type") {
                        if (!value?.id) {
                            return false
                        }
                    } else if (key === "email") {
                        if (value === "" || isValidEmail(value)) {
                            return false
                        }
                    } else if (key === "is_primary" || key === "mapping_id") {
                        continue
                    } else if (!value) {
                        return false
                    }
                }
            }
        }
        return true
    }
    const areAllBillingFieldsFilled = () => {
        const activeFormValues = formValues.filter((item: any) => item.is_active === 1)
        const primaryCount = activeFormValues.filter((item: any) => item.is_primary === 1).length

        if (primaryCount !== 1) {
            return false
        }

        for (const formData of activeFormValues) {
            for (const key in formData) {
                if (formData.hasOwnProperty(key)) {
                    const value = formData[key as keyof FormData]

                    if (key === "pincode") {
                        const isIndia = formData.country?.id === 101

                        if (isIndia) {
                            if (value === "" || value?.toString()?.length !== 6) {
                                return false
                            }
                        }
                        continue
                    } else if (key === "state_id" || key === "city_id") {
                        if (!value?.id) {
                            return false
                        }
                    } else if (key === "gstin") {
                        const gstinValue = value?.toString().trim()
                        const gstRegex = /^[A-Za-z0-9]{15}$/
                        //If GSTIN is not empty, validate it
                        if (gstinValue && !gstRegex.test(gstinValue)) {
                            return false
                        }
                    } else if (["gst_certificate_url", "is_primary", "gst_certificate_path"].includes(key)) {
                        continue
                    } else if (!value) {
                        return false
                    }
                }
            }
        }

        return true
    }

    const toggleDetails = (index: number) => {
        const newShowLocationDetails = [...showLocationDetails]
        newShowLocationDetails[index] = !newShowLocationDetails[index]
        setShowLocationDetails(newShowLocationDetails)
    }

    const toggleBankDetails = (index: number) => {
        const newShowBankDetails = [...showBankDetails]
        newShowBankDetails[index] = !newShowBankDetails[index]
        setShowBankDetails(newShowBankDetails)
    }

    const handleContactChanges = (i: number, e: any, field_name?: string) => {
        const updated = [...contactDetails]
        if (field_name === "phone_full" && e?.phone !== undefined && e?.countryCode !== undefined) {
            const re = /^[0-9\b]+$/
            if (field_name === "phone_full") {
                if (e.phone === "" || re.test(e.phone)) {
                    updated[i]["phone"] = e.phone
                }
            }
            updated[i].country_code = e.countryCode
        } else if (e?.target?.name) {
            updated[i][e.target.name] = e.target.value
        } else if (field_name) {
            updated[i][field_name] = e
        }

        setContactDetails(updated)
    }
    const handleAccountTypeChange = (e: any) => {
        dispatch(setAddAccountForm({ ...addAccountFormValue, account_type: e }))
    }

    const toggleContactDetails = (index: number) => {
        const newShowContactDetails = [...showContactDetails]
        newShowContactDetails[index] = !newShowContactDetails[index]
        setShowContactDetails(newShowContactDetails)
    }
    const changeValues = (e: any) => {
        const alphaNumRegex = /^[A-Za-z0-9]*$/
        const numericRegex = /^[0-9]*$/
        if (e.target.type === "file") {
            dispatch(setAddAccountForm({ ...addAccountFormValue, [e.target.name]: e.target.files[0] }))
        } else if (e.target.name === "ref_id") {
            if (numericRegex.test(e.target.value)) {
                dispatch(setAddAccountForm({ ...addAccountFormValue, [e.target.name]: e.target.value }))
            }
        } else if (e.target.name === "gstin") {
            if (e.target.value.length <= 15 && alphaNumRegex.test(e.target.value)) {
                dispatch(setAddAccountForm({ ...addAccountFormValue, [e.target.name]: e.target.value }))
            }
        } else {
            dispatch(setAddAccountForm({ ...addAccountFormValue, [e.target.name]: e.target.value }))
        }
    }
    const handleToggle = () => {
        setIsChecked(!isChecked)
    }
    useEffect(() => {
        const fetchDefaultCountryStates = async () => {
            const updated = [...formValues]

            for (let i = 0; i < updated.length; i++) {
                if (updated[i].country?.id === 101 && updated[i].shippingStates.length === 0) {
                    const res = await getCountryStates({ country_id: 101 }).unwrap()
                    updated[i].shippingStates = res
                }
            }
            setFormValues(updated)
        }

        fetchDefaultCountryStates()
    }, [])

    const changeStateHandle = (event: any) => {
        let state = event
        dispatch(setAddAccountForm({ ...addAccountFormValue, billing_state: state, billing_city: "" }))
        dispatch(getCities(state?.id))
    }
    const changeCityHandle = (event: any) => {
        let city = event
        dispatch(setAddAccountForm({ ...addAccountFormValue, billing_city: city }))
    }
    const changeAccountHandle = (event: any) => {
        dispatch(setAddAccountForm({ ...addAccountFormValue, account_id: event }))
    }

    const hasChanged = (newObj: any, oldObj: any, keysToCompare: string[]) => {
        return keysToCompare.some(key => {
            const newVal = newObj?.[key]
            const oldVal = oldObj?.[key]

            if (key === "contact_type") {
                const newType = typeof newVal === "object" ? newVal?.title : newVal
                return newType !== oldVal
            }

            if (typeof newVal === "object" && newVal?.id) return newVal?.id !== oldVal
            return newVal !== oldVal
        })
    }

    const handleEditBankDetails = async () => {
        const bankDetailsData = bankDetails.map((item: any) => ({
            bank_name: item.bank_name,
            account_number: item.account_number,
            ifsc_code: item.ifsc_code,
            account_holder_name: item.account_holder_name,
            ...(item.id ? { id: item.id, is_active: item.is_active } : {}),
        }))
        await editBankDetails({
            account_id: ticketData?.id,
            bank_details: bankDetailsData,
        })
    }

    const onSubmit = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        setToggleError(true)

        let BillingData: any[] = []
        if (editFlag) {
            const originalAddresses = ticketData?.billing_addresses || []

            BillingData = formValues
                .map((item, idx) => {
                    const original = originalAddresses.find((orig: any) => orig.id === item.id)

                    // new address
                    if (!item.id) return item

                    // edited address
                    if (
                        original &&
                        hasChanged(item, original, [
                            "country",
                            "address",
                            "pincode",
                            "city_id",
                            "state_id",
                            "gstin",
                            "gst_certificate_url",
                            "is_primary",
                            "is_active",
                        ])
                    )
                        return item

                    return null
                })
                ?.filter(Boolean)

            // Include inActive (deleted) addresses
            BillingData = [...BillingData, ...inActiveAddress]
        } else {
            BillingData = formValues
        }

        const transformedBillingData = BillingData.map((item: any, index) => {
            const shippingData: any = {
                address: item.address,
                pincode: item.pincode,
                country_id: item?.country?.id,
                state_id: item.state_id.id,
                city_id: item.city_id.id,
                is_active: item.is_active,
                is_primary: item?.is_primary,
                gstin: item?.gstin,
                billing_address_index: index + 1,
                ...(editFlag && item?.gst_certificate_url?.[0]
                    ? {
                          old_gst_certificate_url: ticketData.billing_addresses[index]?.gst_certificate_url?.urls?.view,
                          gst_certificate_path: ticketData.billing_addresses[index]?.gst_certificate_path,
                      }
                    : {}),
                ...(item?.gst_certificate_url?.[0]
                    ? {
                          gst_certificate_url: item?.gst_certificate_url[0],
                      }
                    : {}),
            }
            if (editFlag) {
                if (item?.id) {
                    shippingData["id"] = item.id
                }
            }

            return shippingData
        })?.filter((item: any) => item.is_active === 1 || item.id)

        let ContactsData: any[] = []
        if (editFlag) {
            const originalContacts = ticketData?.account_contacts || []

            ContactsData = contactDetails
                .map(item => {
                    const original = originalContacts.find((orig: any) => orig.id === item.id)

                    if (!item.id) return item // new contact

                    if (
                        original &&
                        hasChanged(item, original, [
                            "name",
                            "phone",
                            "email",
                            "contact_type",
                            "is_primary",
                            "is_active",
                            "country_code",
                        ])
                    )
                        return item

                    return null
                })
                ?.filter(Boolean)

            ContactsData = [...ContactsData, ...inActiveContacts]
        } else {
            ContactsData = contactDetails
        }

        const transformedContactData = ContactsData.map((item: any) => {
            const contactData: any = {
                name: item?.name,
                phone: item?.phone,
                email: item?.email,
                contact_type_id: item?.contact_type?.id,
                is_active: item.is_active,
                is_primary: item?.is_primary,
                country_code: item?.country_code,
            }

            if (editFlag && item?.id) {
                contactData["id"] = item.id
                contactData["mapping_id"] = item?.mapping_id
            }
            return contactData
        })?.filter((item: any) => item.is_active === 1 || item.id)
        const structuredData: any = {
            account_name: addAccountFormValue?.account_name,
            legal_business_name: addAccountFormValue?.legal_business_name,
            account_type: addAccountFormValue?.account_type?.id,
            ref_id: addAccountFormValue?.ref_id ?? "",
            billing_addresses: transformedBillingData.map((i, idx) => ({
                ...i,
                ...(Boolean(i.gst_certificate_url)
                    ? { gst_certificate_url: formatFileName(i.gst_certificate_url.name, idx + 1) }
                    : {}),
            })),
        }
        if (
            contactDetails.length === 1 &&
            contactDetails[0].name === "" &&
            contactDetails[0].phone === "" &&
            contactDetails[0].email === "" &&
            contactDetails[0].contact_type?.id === ""
        ) {
            structuredData.account_contacts = []
        } else {
            if (!areAllContactFieldsFilled()) {
                return
            }
            structuredData.account_contacts = transformedContactData
        }
        if (addAccountFormValue?.account_type?.id === "Vendor") {
            if (!!vendorDetails?.pan && !isPanValid()) {
                return
            }
            structuredData.vendor_details = {
                services: vendorDetails?.services?.map((value: any) => value?.id),
                departments: vendorDetails?.department?.map((value: any) => value?.id),
                zone: vendorDetails?.zone?.map((value: any) => value?.id),
                states: vendorDetails?.state?.map((value: any) => value?.id),
                cities: vendorDetails?.city?.map((value: any) => value?.id),
            }
            structuredData.pan = vendorDetails?.pan
            structuredData.msme_reg = Number(vendorDetails?.msme_reg?.id)
            structuredData.msme_reg_number =
                Number(vendorDetails?.msme_reg?.id) === 1 ? vendorDetails?.msme_reg_number : ""
            structuredData.msme_reg_type =
                Number(vendorDetails?.msme_reg?.id) === 1 ? vendorDetails?.msme_reg_type?.id : ""
            structuredData.gst_treatment = vendorDetails?.gst_treatment?.id
        }
        // The prefilled account_id survives an untick of "Link with Parent Account", so the
        // checkbox — not the dropdown value — decides whether the link is sent.
        if (isChecked && addAccountFormValue?.account_type?.id !== "Vendor") {
            // Saving without the id would make the BE clear the existing parent mapping
            if (addAccountFormValue?.account_id?.id === undefined) {
                return
            }
            structuredData.parent_account_id = addAccountFormValue?.account_id?.id
        }
        let response: any
        const isBankDetailsEmpty =
            bankDetails.length === 1 &&
            bankDetails.every(
                (item: any) => item.bank_name === "" && item.account_number === "" && item.ifsc_code === ""
            )

        if (!handleDisable() && areAllBillingFieldsFilled()) {
            if (editFlag && ticketData !== undefined) {
                if (
                    !isBankDetailsEmpty &&
                    !bankDetails.every((item: any) => item.bank_name && item.account_number && item.ifsc_code)
                ) {
                    return
                }
                if (!isBankDetailsEmpty) {
                    handleEditBankDetails()
                }
                structuredData.account_id = ticketData?.id
                response = await editAccount(structuredData)
            } else {
                if (
                    !isBankDetailsEmpty &&
                    !bankDetails.every((item: any) => item.bank_name && item.account_number && item.ifsc_code)
                ) {
                    return
                }

                structuredData.bank_details = isBankDetailsEmpty ? [] : bankDetails
                response = await addAccount(structuredData)
            }

            if (response?.data?.presigned_urls) {
                await Promise.all(
                    transformedBillingData
                        .filter(billingData => billingData.gst_certificate_url)
                        .map(async billingData => {
                            const presignedUrl =
                                response.data.presigned_urls[billingData.billing_address_index][0]?.presigned_url
                            if (presignedUrl) {
                                await axios.put(presignedUrl, billingData.gst_certificate_url, {
                                    headers: {
                                        "Content-Type": billingData.gst_certificate_url.type,
                                    },
                                })
                            }
                        })
                )
            }
            if (response?.data) {
                onSuccess?.()
                toggle()
            }
        }
    }

    /**
     * To append the file index after the file name
     * @param {string} fileName - The full file name with extension
     * @param {number} index - The index to append
     * @return {string} fileName_[index].ext
     */
    const formatFileName = (fileName: string, index: number) => {
        const lastDotIndex = fileName?.lastIndexOf(".")

        // If there's no extension, return with index appended
        if (lastDotIndex === -1) {
            return `${fileName}_${index}`
        }

        const namePart = fileName?.substring(0, lastDotIndex)
        const extension = fileName?.substring(lastDotIndex)

        return `${namePart}_${index}${extension}`
    }

    const resetAddAccountData = () => {
        dispatch(
            setAddAccountForm({
                ...addAccountFormValue,
                account_name: "",
                account_id: {},
                account_type: "",
                legal_business_name: "",
                ref_id: "",
            })
        )
        setIsChecked(false)
        setContactDetails([
            {
                name: "",
                phone: "",
                email: "",
                contact_type: { id: "", title: "" },
                country_code: "+91",
                mapping_id: "",
                is_active: 1,
                is_primary: 0,
            },
        ])
        setBankDetails([
            {
                bank_name: "",
                account_number: "",
                ifsc_code: "",
                account_holder_name: "",
            },
        ])
        setFormValues([
            {
                country: { id: 101, title: "India" },
                address: "",
                pincode: "",
                city_id: { id: "", title: "" },
                state_id: { id: "", title: "" },
                shippingCities: [],
                shippingStates: [],
                gstin: "",
                gst_certificate_url: "",
                gst_certificate_path: "",
                is_active: 1,
                is_primary: 0,
            },
        ])
    }

    const isPanValid = () => {
        return vendorDetails?.pan?.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(vendorDetails?.pan)
    }
    const getCountryTitleById = (id: number) => {
        return Countries.find(country => country.id === id)?.title || "-"
    }
    const PrefillBilling = async (ticketData: any) => {
        const updatedBillingAddress = await Promise.all(
            ticketData?.billing_addresses
                ?.filter((item: any) => item.is_active === 1)
                ?.map(async (address: any, index: number) => {
                    const statesResponse = await getCountryStates({ country_id: address?.country_id })
                    const citiesResponse = await dispatch(getDestinationCities(address?.state_id))
                    const updatedFormValues = [...formValues]
                    updatedFormValues[index] = {
                        shippingCities: citiesResponse?.payload || [],
                        shippingStates: statesResponse?.data || [],
                        city_id: { id: address?.city_id, title: address?.city },
                        state_id: { id: address?.state_id, title: address?.state },
                        country: { id: address?.country_id, title: getCountryTitleById(address?.country_id) },
                        address: address.address,
                        pincode: address.pincode,
                        is_active: address.is_active,
                        is_primary: address?.is_primary,
                        gstin: address?.gstin,
                        id: address?.id,
                        gst_certificate_url: address.gst_certificate_url,
                        gst_certificate_path: address?.gst_certificate_path,
                    }

                    return updatedFormValues[index]
                })
        )
        setFormValues(updatedBillingAddress)
    }
    useEffect(() => {
        getCountryStates({ zone: vendorDetails?.zone?.map((value: any) => value?.id) })
    }, [vendorDetails?.zone])

    useEffect(() => {
        if (editFlag && ticketData !== undefined) {
            setIsChecked(!ticketData?.parent_account_name ? false : true)
            dispatch(getCities(ticketData?.state_id))
            dispatch(
                setAddAccountForm({
                    ...addAccountFormValue,
                    account_name: ticketData?.account_name,
                    legal_business_name: ticketData?.legal_business_name,
                    account_type: { id: ticketData?.account_type, title: ticketData?.account_type },
                    ref_id: ticketData?.reference_id ?? "",
                    account_id: {
                        // list_accounts returns parent_account_id; other sources only carry the name
                        id:
                            ticketData?.parent_account_id ??
                            getLinkedAccountData?.find(
                                (data: any) => data?.account_name === ticketData?.parent_account_name
                            )?.id,
                        title: ticketData?.parent_account_name,
                    },
                })
            )
            PrefillBilling(ticketData)

            const activeContacts = ticketData?.account_contacts?.filter((contact: any) => contact.is_active === 1)
            const updatedContacts = activeContacts?.length
                ? activeContacts.map((contact: any) => {
                      const matched = contactTypes?.find((type: any) => type?.id === contact?.contact_type_id)
                      return {
                          ...contact,
                          country_code: contact?.country_code || "+91",
                          mapping_id: contact?.mapping_id,
                          contact_type: {
                              id: matched?.id ?? "",
                              title: matched?.contact_type
                                  ? matched.contact_type.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
                                  : contact?.contact_type ?? "",
                          },
                          is_primary: contact?.is_primary,
                      }
                  })
                : [
                      {
                          name: "",
                          phone: "",
                          email: "",
                          contact_type: { id: "", title: "" },
                          country_code: "+91",
                          mapping_id: "",
                          is_active: 1,
                          is_primary: 0,
                      },
                  ]
            setContactDetails(updatedContacts)
        }
    }, [ticketData])

    // When ticketData has no parent_account_id (e.g. account details API in the project form), the
    // prefill above runs before get_accounts_compact resolves on a cold cache, leaving the parent's
    // id undefined — resolve it once the list arrives.
    useEffect(() => {
        if (!editFlag || !ticketData?.parent_account_name || !getLinkedAccountData) return
        const currentParent = addAccountFormValue?.account_id
        // Only fill the untouched prefill; don't override a parent the user picked
        if (currentParent?.id || currentParent?.title !== ticketData?.parent_account_name) return
        const parent = getLinkedAccountData.find((data: any) => data?.account_name === ticketData?.parent_account_name)
        if (!parent) return
        dispatch(
            setAddAccountForm({
                ...addAccountFormValue,
                account_id: { id: parent.id, title: parent.account_name },
            })
        )
    }, [getLinkedAccountData, ticketData, editFlag])

    useEffect(() => {
        if (editFlag && bankDetailsData) {
            if (bankDetailsData?.length === 0) {
                setBankDetails([
                    {
                        bank_name: "",
                        account_number: "",
                        ifsc_code: "",
                        account_holder_name: "",
                    },
                ])
            } else {
                setBankDetails(bankDetailsData || [])
            }
        }
    }, [bankDetailsData, editFlag])

    useEffect(() => {
        const activeFormValues = formValues?.filter(item => item.is_active === 1)

        if (activeFormValues.length === 1) {
            const updatedFormValues = formValues.map(item => {
                if (item.is_active === 1 && item.is_primary !== 1) {
                    return { ...item, is_primary: 1 }
                }
                return item
            })

            if (JSON.stringify(updatedFormValues) !== JSON.stringify(formValues)) {
                setFormValues(updatedFormValues)
            }
        }
    }, [formValues])

    useEffect(() => {
        const activeFormValues = contactDetails.filter(item => item.is_active === 1)

        if (activeFormValues.length === 1) {
            const updatedFormValues = contactDetails.map(item => {
                if (item.is_active === 1 && item.is_primary !== 1) {
                    return { ...item, is_primary: 1 }
                }
                return item
            })

            if (JSON.stringify(updatedFormValues) !== JSON.stringify(contactDetails)) {
                setContactDetails(updatedFormValues)
            }
        }
    }, [contactDetails])

    useEffect(() => {
        if (!prefillDone) return

        if (!vendorDetails.zone?.length) {
            setVendorDetails(prev => ({
                ...prev,
                state: [],
                city: [],
            }))
            return
        }

        getCountryStates({ zone: vendorDetails?.zone?.map((z: any) => z.id) })
            .unwrap()
            .then((res: any) => {
                const validStates = res || []
                const validStateIds = validStates.map((state: any) => state.id)

                const filteredStates = vendorDetails.state?.filter((state: any) => validStateIds.includes(state.id))

                setVendorDetails(prev => ({
                    ...prev,
                    state: filteredStates,
                }))
            })
    }, [vendorDetails.zone, prefillDone])

    useEffect(() => {
        if (!prefillDone) return
        if (!vendorDetails.state?.length) {
            setVendorDetails(prev => ({
                ...prev,
                city: [],
            }))
            return
        }

        if (!vendorCitiesList || !vendorDetails.city?.length) return

        const validCityIds = vendorCitiesList.map((city: any) => city.id)

        const updatedCities = vendorDetails.city.filter((city: any) => validCityIds.includes(city.id))

        if (updatedCities.length !== vendorDetails.city.length) {
            setVendorDetails(prev => ({
                ...prev,
                city: updatedCities,
            }))
        }
    }, [vendorCitiesList, prefillDone, vendorDetails?.state])

    useEffect(() => {
        if (editFlag && ticketData) {
            const zones = Object.keys(ticketData?.zones || {}).map(zone => ({
                id: zone,
                title: zone,
            }))

            const stateMap = new Map()
            const cityMap = new Map()

            Object.entries(ticketData?.zones || {}).forEach(([zone, entries]) => {
                if (!Array.isArray(entries)) return

                entries.forEach(({ state, state_id, city, city_id }) => {
                    if (!stateMap.has(state_id)) {
                        stateMap.set(state_id, { id: state_id, title: state })
                    }

                    if (!cityMap.has(city_id)) {
                        cityMap.set(city_id, { id: city_id, title: city })
                    }
                })
            })

            const Vendorstates = Array.from(stateMap.values())
            const Vendorcities = Array.from(cityMap.values())

            setVendorDetails((prev: any) => ({
                ...prev,
                zone: zones,
                state: Vendorstates,
                city: Vendorcities,
                department: ticketData.departments?.map((dept: string) => ({
                    id: dept,
                    title: dept,
                })),
                services: ticketData?.services?.map((data: any) => {
                    return { id: data?.id, title: data?.name }
                }),
                pan: ticketData?.pan,
                msme_reg: { id: ticketData?.msme_reg?.toString(), title: ticketData?.msme_reg === 1 ? "Yes" : "No" },
                msme_reg_number: ticketData?.msme_reg_number,
                msme_reg_type: { id: ticketData?.msme_reg_type, title: ticketData?.msme_reg_type },
                gst_treatment: { id: ticketData?.gst_treatment, title: ticketData?.gst_treatment },
            }))
            setPrefillDone(true)
        }
    }, [ticketData, editFlag])

    useEffect(() => {
        if (!addAllAccountsPermission && !editFlag) {
            if (addVendorAccountsPermission) {
                handleAccountTypeChange({ id: "Vendor", title: "Vendor" })
            } else if (addClientAccountsPermission) {
                handleAccountTypeChange({ id: "Client", title: "Client" })
            }
        }
    }, [addAllAccountsPermission, addVendorAccountsPermission, addClientAccountsPermission, editFlag])

    return {
        addStates: {
            addAccountFormValue,
            toggleError,
            isChecked,
            formValues,
            showLocationDetails,
            showContactDetails,
            contactDetails,
            getLinkedAccountData,
            isAddAccountLoading,
            isAddAccountSuccess,
            cities,
            isAccountLoading,
            isAccountSuccess,
            vendorDetails,
            vendorStateList,
            vendorCitiesList,
            bankDetails,
            showBankDetails,
            addAllAccountsPermission,
        },
        addfunctions: {
            setAddAccountForm,
            setToggleError,
            changeStateHandle,
            setFormValues,
            handleChanges,
            areAllContactFieldsFilled,
            areAllBillingFieldsFilled,
            toggleDetails,
            setShowLocationDetails,
            changeCityHandle,
            changeValues,
            onSubmit,
            handleTogglePrimary,
            handleContactTogglePrimary,
            handleToggle,
            setIsChecked,
            handleFileChanges,
            resetAddAccountData,
            addFormFields,
            removeFormFields,
            setShowContactDetails,
            setContactDetails,
            removeContactFields,
            addContactFields,
            changeAccountHandle,
            toggleContactDetails,
            handleContactChanges,
            changeshippingCityHandle,
            changeshippingStateHandle,
            handleDisable,
            handleAccountTypeChange,
            setVendorDetails,
            addBankDetails,
            removeBankDetails,
            handleBankChanges,
            toggleBankDetails,
            isPanValid,
            handleCountryChanges,
        },
    }
}
export default useAddAccounts
